import React, { useEffect, useRef, useState } from "react";
import { BoardButton } from "../../atoms/BoardDesignSystem";
import BoardAttachmentViewer from "../BoardAttachmentViewer/BoardAttachmentViewer";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  BOARD_ATTACHMENT_ACCEPT,
  isImageAttachment,
  isPdfAttachment,
  validateBoardAttachmentFile,
} from "../../../app/utils/projectBoard/projectBoardAttachment";
import "./BoardAttachmentPanel.scss";

const formatSize = (kb) => {
  if (!kb) return "";
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const AttachmentThumb = ({ item }) => {
  if (item.thumbnailUrl) {
    return (
      <img
        src={item.thumbnailUrl}
        alt=""
        className={`board-attachment-panel__thumb${
          isPdfAttachment(item.fileType) ? " board-attachment-panel__thumb--pdf" : ""
        }`}
        loading="lazy"
      />
    );
  }
  if (isImageAttachment(item.fileType)) {
    return <span className="board-attachment-panel__file-icon">🖼</span>;
  }
  if (isPdfAttachment(item.fileType)) {
    return <span className="board-attachment-panel__file-icon">📄</span>;
  }
  return <span className="board-attachment-panel__file-icon">📎</span>;
};

const BoardAttachmentPanel = ({
  cardId,
  attachmentCount = 0,
  disabled = false,
  canUpload = false,
  compact = false,
  onUpload,
  onUploaded,
  previewAttachment = null,
  onPreviewAttachment,
}) => {
  const inputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);

  const activePreview = previewAttachment ?? localPreview;
  const setActivePreview = onPreviewAttachment ?? setLocalPreview;

  useEffect(() => {
    if (!cardId || !onUpload?.fetchAttachments) {
      setAttachments([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    onUpload
      .fetchAttachments(cardId)
      .then((items) => {
        if (!cancelled) setAttachments(items);
      })
      .catch(() => {
        if (!cancelled) setAttachments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cardId, attachmentCount, onUpload]);

  const uploadFile = async (file) => {
    const check = validateBoardAttachmentFile(file);
    if (!check.ok) {
      showErrorToast(check.message);
      return;
    }
    if (!canUpload || uploading || !onUpload?.upload) return;

    setUploading(true);
    try {
      const uploaded = await onUpload.upload(cardId, file);
      showSuccessToast("Attachment uploaded.");
      if (uploaded) {
        setAttachments((prev) => {
          const exists = prev.some((item) => item.attachmentId === uploaded.attachmentId);
          if (exists) return prev;
          return [uploaded, ...prev];
        });
      } else {
        const items = await onUpload.fetchAttachments(cardId);
        setAttachments(items);
      }
      onUploaded?.(uploaded);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to upload attachment.";
      showErrorToast(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await uploadFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (!canUpload || disabled || uploading) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const openPreview = (item) => {
    if (!item?.attachmentId || !cardId) return;
    setActivePreview({ ...item, cardId });
  };

  return (
    <>
      <div
        className={`board-attachment-panel ${compact ? "board-attachment-panel--compact" : ""}`}
        onDragOver={(e) => {
          if (canUpload) e.preventDefault();
        }}
        onDrop={handleDrop}
      >
        {loading && <p className="board-attachment-panel__status">Loading attachments...</p>}

        {!loading && attachments.length > 0 && (
          <ul className="board-attachment-panel__list">
            {attachments.map((item) => (
              <li key={item.attachmentId} className="board-attachment-panel__item">
                <button
                  type="button"
                  className="board-attachment-panel__link"
                  onClick={() => openPreview(item)}
                  title={`View ${item.fileName}`}
                >
                  <AttachmentThumb item={item} />
                  <span className="board-attachment-panel__meta">
                    <span className="board-attachment-panel__name">{item.fileName}</span>
                    <span className="board-attachment-panel__sub">
                      {item.uploader?.name || "Unknown"}
                      {item.fileSizeKb ? ` · ${formatSize(item.fileSizeKb)}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {!loading && attachments.length === 0 && attachmentCount > 0 && (
          <p className="board-attachment-panel__count">
            {attachmentCount} attachment{attachmentCount !== 1 ? "s" : ""} on this card
          </p>
        )}

        {canUpload && (
          <>
            <input
              ref={inputRef}
              type="file"
              className="board-attachment-panel__input"
              accept={BOARD_ATTACHMENT_ACCEPT}
              onChange={handleFileChange}
              disabled={disabled || uploading}
            />
            <BoardButton
              variant="secondary"
              block
              disabled={disabled || uploading}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? "Uploading..." : "Upload attachment"}
            </BoardButton>
          </>
        )}

        {!canUpload && !loading && attachments.length === 0 && (
          <p className="board-attachment-panel__note">No attachments on this card.</p>
        )}
      </div>

      {activePreview && !onPreviewAttachment && (
        <BoardAttachmentViewer
          attachment={activePreview}
          onClose={() => setLocalPreview(null)}
        />
      )}
    </>
  );
};

export default BoardAttachmentPanel;
